import React, { FC, useState } from "react";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { BaseCheckboxInput } from "@/components/VnCheckboxInput/BaseCheckboxInput";
import {
  VnDialog,
  VnDialogContent,
  VnDialogFooter,
  VnDialogTitle,
} from "@/components/VnDialog";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import {
  AddressUpdateType,
  HouseholdMemberType,
  HouseholdMemberUpdateType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { formatHouseholdMemberName } from "@/utils/householdMember";

const addressTypeKeyMap: Record<AddressUpdateType, I18nKeyType> = {
  home: "native:applicationAddressScreen.updateHouseholdAddressesDialog.addressTypeHome",
  mailing:
    "native:applicationAddressScreen.updateHouseholdAddressesDialog.addressTypeMailing",
  homeAndMailing:
    "native:applicationAddressScreen.updateHouseholdAddressesDialog.addressTypeHomeAndMailing",
};

export type AffectedMemberType = {
  member: HouseholdMemberType;
  addressUpdate: HouseholdMemberUpdateType;
};

type UpdateHouseholdAddressesDialogPropsType = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  affectedMembers: AffectedMemberType[];
  onCompleted: () => void;
  primaryApplicantId: string;
  primaryApplicantUpdate: HouseholdMemberUpdateType;
  addressType: AddressUpdateType;
};

export const UpdateHouseholdAddressesDialog: FC<
  UpdateHouseholdAddressesDialogPropsType
> = ({
  open,
  onOpenChange,
  affectedMembers,
  onCompleted,
  primaryApplicantId,
  primaryApplicantUpdate,
  addressType,
}) => {
  const { mutateAsync: updateHouseholdMemberAsync, isPending } =
    useUpdateHouseholdMemberMutation();

  const { t } = useTranslator();

  const translatedAddressType = t(addressTypeKeyMap[addressType]);

  const [checkedIds, setCheckedIds] = useState<Set<string>>(
    () => new Set(affectedMembers.map(({ member }) => member.id)),
  );

  // Reset selections whenever the dialog opens with a fresh set of affected members
  React.useEffect(() => {
    if (open) {
      setCheckedIds(new Set(affectedMembers.map(({ member }) => member.id)));
    }
  }, [open, affectedMembers]);

  const toggleMember = (id: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectedCount = checkedIds.size;

  const handleUpdate = async () => {
    try {
      // Save the primary applicant's address first, then update selected members in parallel
      await updateHouseholdMemberAsync({
        id: primaryApplicantId,
        householdMemberData: primaryApplicantUpdate,
      });

      const selected = affectedMembers.filter(({ member }) =>
        checkedIds.has(member.id),
      );

      await Promise.allSettled(
        selected.map(({ member, addressUpdate }) =>
          updateHouseholdMemberAsync({
            id: member.id,
            householdMemberData: addressUpdate,
          }),
        ),
      );
    } finally {
      onOpenChange(false);
      onCompleted();
    }
  };

  const handleSkip = async () => {
    try {
      await updateHouseholdMemberAsync({
        id: primaryApplicantId,
        householdMemberData: primaryApplicantUpdate,
      });
    } finally {
      onOpenChange(false);
      onCompleted();
    }
  };

  if (affectedMembers.length === 0) return null;

  return (
    <VnDialog open={open} onOpenChange={onOpenChange}>
      <VnDialogContent>
        <VnDialogTitle i18nKey="native:applicationAddressScreen.updateHouseholdAddressesDialog.title" />
        <VnText
          variant="md"
          className="text-muted-foreground"
          i18nKey={
            "native:applicationAddressScreen.updateHouseholdAddressesDialog.description" as I18nKeyType
          }
          i18nOptions={{
            householdName: formatHouseholdMemberName(
              affectedMembers[0]!.member,
            ),
            count: affectedMembers.length - 1,
            addressType: translatedAddressType,
          }}
        />

        <VnBox className="gap-3">
          {affectedMembers.map(({ member }) => (
            <VnBox key={member.id} className="flex flex-row items-center gap-3">
              <BaseCheckboxInput
                checked={checkedIds.has(member.id)}
                onCheckedChange={() => toggleMember(member.id)}
              />
              <VnText variant="md">{formatHouseholdMemberName(member)}</VnText>
            </VnBox>
          ))}
        </VnBox>

        <VnDialogFooter>
          <VnButton
            variant="outline"
            className="flex-1"
            onPress={() => {
              void handleSkip();
            }}
            i18nKey="native:applicationAddressScreen.updateHouseholdAddressesDialog.onlyUpdateMine"
          />
          <VnButton
            className="flex-1"
            isLoading={isPending}
            isDisabled={selectedCount === 0}
            onPress={() => {
              void handleUpdate();
            }}
            i18nKey={
              "native:applicationAddressScreen.updateHouseholdAddressesDialog.updateMembers" as I18nKeyType
            }
            i18nOptions={{ count: selectedCount }}
          />
        </VnDialogFooter>
      </VnDialogContent>
    </VnDialog>
  );
};
